import { gql } from "lib/apolloClient";
import {
  categoryWiseAssessmentsFragment,
  organizationAssignmentsFragment,
  courseUserMapFragment,
  gradebookAssignmentsFragment,
  organizationFragment,
  categoriesItemFragment,
  studentRatingsFragment,
  gradebookAssignmentFragment,
  basicStaffDetailsFragment,
} from "./GradebookFragments";
import { gradesItemFragment } from "ScoreCategories/modules/ScoreCategoriesFragments";
import { cumulativeTermWeightsFragment } from "GradingSetupV2/modules/GradingSetupV2Fragments";

export const getCategoryWiseAssessmentsQuery = gql`
  query getCategoryWiseAssessments(
    $id: ID!
    $grades: [ID!]
    $assessmentToolTypes: [ASSESSMENT_EVALUATION_TYPE_ENUM!]
    $academicCriteriaSetsFilter: academicCriteriaSetsFilter
    $assignmentFilters: AssignmentFeedFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        constants {
          academicCriteriaSets(
            grades: $grades
            filters: $academicCriteriaSetsFilter
          ) {
            id
            label
            isShowGradeBoundaries
            academicCriteriaValues {
              id
              label
              color
              scoreValues {
                id
                value
                genericTag {
                  id
                  label
                }
                category {
                  id
                }
              }
              gradeBoundaries {
                id
              }
              gradeScaleType
              ...categoryWiseAssessmentItem
            }
          }
        }
      }
    }
  }
  ${categoryWiseAssessmentsFragment.assignmentsFragment}
`;

export const getStudentWiseRatingsQuery = gql`
  query getStudentWiseRatings(
    $id: ID!
    $courseUserMapFilters: CourseUserMapFilter
    $filtersForGeneric: ibPYPElementRatingFiltersForGeneric
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        userMap(filters: $courseUserMapFilters) {
          edges {
            node {
              id
              user {
                id
                ... on Student {
                  ...StudentRatingsItem
                }
              }
              tags {
                id
                label
                abbreviation
                reportDisplayName
              }
              subjectWiseLevel {
                level {
                  id
                  label
                  abbreviation
                  reportDisplayName
                }
                subject {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentRatingsFragment.studentRatings}
`;

export const getCourseAcademicYearTermsQuery = gql`
  query getCourseAcademicYearTerms(
    $id: ID!
    $curriculumProgramIds: [ID!]
    $learningCourseIds: [ID!]
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        academicYears {
          id
          isCurrentAcademicYear
          startDate
          endDate
          terms(
            filters: {
              learningCourseIds: $learningCourseIds
              curriculumProgramIds: $curriculumProgramIds
            }
          ) {
            id
            label
            startDate
            endDate
            type
            lockingStatus
            associatedAcademicTermEntities {
              id
              academicTerm {
                id
                label
                type
                startDate
              }
              cumulativeAcademicTermWeightages {
                id
                weightage
                percentage
              }
            }
          }
        }
      }
    }
  }
`;

export const getGradebookSummaryExportQuery = gql`
  query getGradebookSummaryExport(
    $id: ID!
    $input: [GradebookSummaryExportInput!]
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        gradebookSummaryExport(input: $input)
      }
      __typename
    }
  }
`;

export const getAdminGradebookSummaryExportQuery = gql`
  query getAdminGradebookSummaryExport(
    $id: ID!
    $input: [GradebookSummaryExportOrganizationInput!]
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        gradebookSummaryExport(input: $input)
      }
    }
  }
`;

export const getCourseGradesQuery = gql`
  query getCourseGrades($id: ID!, $courseGradeFilters: CourseGradeFilters) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        grades(filters: $courseGradeFilters) {
          id
          name
          displaySequence
        }
        primaryGrade(filters: $courseGradeFilters) {
          id
          name
        }
      }
    }
  }
`;

export const getOrganizationAssignmentsGradebookQuery = gql`
  query getOrganizationAssignmentsProgressSummary(
    $organizationId: ID!
    $courseIds: [ID!]
    $subjectIds: [ID!]
    $states: [ASSIGNMENT_STATE_ENUM!]
    $categoryIds: [ID!]
    $searchText: String
    $academicTermIds: [ID!]
    $assessmentTypes: [String!]
    $excludedAssessmentTypes: [String!]
    $groupTypes: [ASSESSMENT_TYPE_ENUM!]
    $isTeacherEvaluationEnabled: Boolean
    $isAssessmentScoringEnabled: Boolean
    $contentTypes: [ASSIGNMENT_CONTENT_TYPE_ENUM!]
    $assessmentToolTypes: [ASSESSMENT_EVALUATION_TYPE_ENUM!]
    $includedStudentIds: [ID!]
    $primaryGradeId: ID
    $isExcludedFromFinalGradeCalculation: Boolean
    $first: Int
    $after: String
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationAssignmentItem
      }
    }
  }
  ${organizationAssignmentsFragment.organizationAssignmentProgressSummary}
`;

export const getStudentIbGradeRatingsQuery = gql`
  query getStudentIbGradeRatingsQuery(
    $id: ID!
    $filtersForRatingHistory: ibPYPElementRatingFiltersForRatingHistory
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        ibPYPElementRatings(
          dataSet: RATING_HISTORY
          filtersForRatingHistory: $filtersForRatingHistory
        ) {
          id
          ibPYPElementId
          value
          academicCriteriaValue {
            id
            label
          }
          publishedAt
          parent {
            id
            ... on StudentAssignment {
              id
              assignment {
                id
                content {
                  id
                  ... on Assessment {
                    id
                    title {
                      id
                      value
                    }
                    assessmentType {
                      id
                      value
                    }
                    category {
                      id
                      label
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationCoursesQuery = gql`
  query getOrganizationCourses(
    $organizationId: ID!
    $courseFilters: CourseFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        courses(
          orderBy: TITLE
          orderByDirection: ASC
          filters: $courseFilters
        ) {
          edges {
            node {
              id
              title
              subjects {
                id
              }
              primaryGrade {
                id
              }
              allCollaborators {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentAssignmentsQuery = gql`
  query getStudentAssignments(
    $id: ID!
    $filters: StudentAssignmentFeedFilters
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        id
        assignments(filters: $filters) {
          edges {
            node {
              id
              assignment {
                id
                isExcludedFromFinalGradeCalculation
                content {
                  ... on Assessment {
                    id
                    title {
                      id
                      value
                    }
                    category {
                      id
                      label
                    }
                    assessmentType {
                      id
                      value
                    }
                    assessmentTools(filters: { assessmentToolType: SCORE }) {
                      edges {
                        node {
                          id
                          assessmentToolItem {
                            ... on ScoreAssessmentTool {
                              id
                              scoreToolData {
                                id
                                maxScore
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              ibPYPElementRatings {
                id
                value
                ibPYPElementType
              }
            }
          }
        }
      }
    }
  }
`;

export const getAcademicCriteriaSetsQuery = gql`
  query getFinalScoreCriteriaSet(
    $id: ID!
    $filters: academicCriteriaSetsFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        constants {
          academicCriteriaSets(filters: $filters) {
            id
            label
            criteriaType
            subType
            academicCriteriaValues {
              id
              label
              abbreviation
              displaySequence
              boundaryGuidelines
              description
              color
              localGrades {
                id
                label
                grade {
                  id
                  name
                }
                localGradeCriteriaValue {
                  id
                  label
                }
              }
              scoreValues {
                id
                minValue
                maxValue
                value
              }
            }
            curriculumProgram {
              id
            }
          }
        }
      }
    }
  }
`;

export const getCourseUserMapQuery = gql`
  query getCourseUserMap(
    $id: ID!
    $filters: CourseUserMapFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseItem
      }
    }
  }
  ${courseUserMapFragment.courseUserMap}
`;

export const getGradebookAssignmentsQuery = gql`
  query getGradebookAssignments(
    $organizationId: ID!
    $first: Int
    $after: String
    $assignmentFeedFilters: AssignmentFeedFilters
    $assessmentToolsFilters: AssessmentToolFilters
    $customOrderBy: [CustomOrderByInput!]
    $aclControlParametersFilters: ACLControlParameterFilter
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationAssignmentItem
      }
    }
  }
  ${gradebookAssignmentsFragment.gradebookAssignments}
`;

export const getGradebookSingleAssignmentQuery = gql`
  query getGradebookSingleAssignmentQuery(
    $id: ID!
    $assessmentToolsFilters: AssessmentToolFilters
    $aclControlParametersFilters: ACLControlParameterFilter
  ) {
    node(id: $id, type: ASSIGNMENT) {
      id
      ... on Assignment {
        ...assignmentProgressSummaryItem
        __typename
      }
      __typename
    }
  }

  ${gradebookAssignmentFragment.gradebookAssignment}
`;

export const getOrganizationPointSetQuery = gql`
  query getOrganizationPointSet(
    $id: ID!
    $filters: PointAssessmentSetFilter
    $assignmentFeedFilters: AssignmentFeedFilters
    $skipAssignments: Boolean = true
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        pointAssessmentSets(filters: $filters) {
          totalCount
          edges {
            node {
              id
              label
              type
              subType
              isShowGradeBoundaries
              associatedEntities {
                id
                ... on Course {
                  id
                  title
                }
                ... on Subject {
                  id
                  name
                }
                ... on Grade {
                  ...gradeItem
                }

                ... on AcademicTerm {
                  id
                  label
                }
              }
              categories {
                ...categoryItem
              }
              updatedAt
              updatedBy {
                ...basicStaffDetails
              }
              createdBy {
                ...basicStaffDetails
              }
            }
          }
        }
      }
    }
  }
  ${basicStaffDetailsFragment}
  ${gradesItemFragment.gradeFragment}
  ${categoriesItemFragment.categoryItem}
`;

export const getOrganizationSubjectsQuery = gql`
  query getOrganizationSubjects(
    $id: ID!
    $filters: SubjectFilterInput
    $assignmentCountFilters: SubjectAssignmentsFilters
    $curriculumProgramType: CURRICULUM_PROGRAM_TYPE_ENUM!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationItem
      }
    }
  }
  ${organizationFragment.organizationSubjects}
`;

export const getEvaluatedButNotSharedAssignmentsCountByTermQuery = gql`
  query getEvaluatedButNotSharedAssignmentsCountByTerm(
    $id: ID!
    $termFilters: TermFilters
    $assignmentFeedFilters: AssignmentFeedFilters
  ) {
    node(id: $id, type: ACADEMIC_YEAR) {
      ... on AcademicYear {
        id
        terms(filters: $termFilters) {
          id
          assignments(filters: $assignmentFeedFilters) {
            totalCount
          }
        }
      }
    }
  }
`;

export const getStudentsElementRatingsQuery = gql`
  query getStudentsElementRatings(
    $studentIds: [ID!]
    $startDate: String
    $endDate: String
    $filtersForRecentMost: ibPYPElementRatingFilterForRecentMost
  ) {
    multiNode(ids: $studentIds, type: STUDENT) {
      id
      ... on Student {
        id
        ibPYPElementRatings(
          dataSet: MOST_RECENT
          startDate: $startDate
          endDate: $endDate
          filtersForRecentMost: $filtersForRecentMost
        ) {
          id
          ibPYPElementId
          ibPYPElementType
          academicCriteriaSet {
            id
          }
          academicCriteriaValue {
            id
          }
          isComputedRating
        }
      }
    }
  }
`;

export const getStudentElementRemarksQuery = gql`
  query getStudentElementRemarks(
    $id: ID!
    $courseUserMapFilters: CourseUserMapFilter
    $elementRemarksFilters: ibPypElementRemarkFilter
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        userMap(filters: $courseUserMapFilters) {
          edges {
            node {
              id
              user {
                id
                ... on Student {
                  id
                  ibPypElementRemarks(filters: $elementRemarksFilters) {
                    id
                    ibPYPElementId
                    ibPYPElementType
                    parentType
                    message
                    remarkType
                    genericTag {
                      id
                    }
                    academicTerm {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getLearningCoursesSubjectsQuery = gql`
  query getLearningCoursesSubjectsQuery($id: ID!) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        subjects {
          id
          name
        }
      }
    }
  }
`;

export const getCourseCumulativeTermWeightsQuery = gql`
  query getCourseCumulativeTermWeights(
    $id: ID!
    $filters: GradingConfigurationFilters
  ) {
    node(id: $id, type: LEARNING_COURSE) {
      id
      ... on LearningCourse {
        id
        gradingConfigurations(filters: $filters) {
          ...CumulativeTermWeightsFragment
        }
      }
    }
  }
  ${cumulativeTermWeightsFragment}
`;

export const getSubjectCumulativeTermWeightsQuery = gql`
  query getSubjectCumulativeTermWeights(
    $id: ID!
    $filters: SubjectGradingConfigurationFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        gradingConfigurations(filters: $filters) {
          ...CumulativeTermWeightsFragment
        }
      }
    }
  }
  ${cumulativeTermWeightsFragment}
`;

export const getPlannerElementNodeSetWithGradeScalesQuery = gql`
  query getPlannerElementNodeSetWithGradeScales(
    $id: ID!
    $plannerElementNodeSetsFilters: PlannerElementNodeSetFilters
    $gradeScaleEntityDetailFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetsFilters) {
          edges {
            node {
              id
              type
              title
              gradeScales {
                id
                type
                displayName
                templateName
                gradeScaleEntityDetails(
                  filters: $gradeScaleEntityDetailFilters
                ) {
                  id
                  gradeScaleValues {
                    id
                    label
                    abbreviation
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
